import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { CMVReportData } from "@/hooks/useCMVReport";

interface ExportParams {
  data: CMVReportData;
  dateFrom: Date;
  dateTo: Date;
  restaurantName: string;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

export function exportCMVToPDF({ data, dateFrom, dateTo, restaurantName }: ExportParams) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPosition = 20;

  // Title
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Relatório CMV - Custo de Mercadoria Vendida", pageWidth / 2, yPosition, {
    align: "center",
  });

  yPosition += 10;
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text(restaurantName, pageWidth / 2, yPosition, { align: "center" });

  yPosition += 8;
  doc.setFontSize(10);
  doc.text(
    `Período: ${format(dateFrom, "dd/MM/yyyy", { locale: ptBR })} a ${format(
      dateTo,
      "dd/MM/yyyy",
      { locale: ptBR }
    )}`,
    pageWidth / 2,
    yPosition,
    { align: "center" }
  );

  // Summary Section
  yPosition += 15;
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Resumo", 14, yPosition);

  yPosition += 8;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  const summaryData = [
    ["Receita Total:", formatCurrency(data.totalRevenue)],
    ["Custo Total (CMV):", formatCurrency(data.totalCost)],
    ["Margem Bruta:", formatCurrency(data.totalMargin)],
    ["% Margem:", `${data.marginPercentage.toFixed(1)}%`],
  ];

  summaryData.forEach(([label, value]) => {
    doc.text(label, 14, yPosition);
    doc.text(value, 80, yPosition);
    yPosition += 6;
  });

  // Products Section
  yPosition += 10;
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("CMV por Produto", 14, yPosition);

  yPosition += 8;
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");

  // Table header
  const headers = ["Produto", "Qtd", "Receita", "Custo", "Margem", "% Margem"];
  const colWidths = [50, 15, 30, 30, 30, 25];
  let xPos = 14;

  headers.forEach((header, i) => {
    doc.text(header, xPos, yPosition);
    xPos += colWidths[i];
  });

  yPosition += 5;
  doc.setFont("helvetica", "normal");

  // Table rows
  data.productsCMV.slice(0, 20).forEach((product) => {
    if (yPosition > 270) {
      doc.addPage();
      yPosition = 20;
    }

    xPos = 14;
    const rowData = [
      product.productName.substring(0, 25),
      product.quantitySold.toString(),
      formatCurrency(product.totalRevenue),
      formatCurrency(product.totalCost),
      formatCurrency(product.margin),
      `${product.marginPercentage.toFixed(1)}%`,
    ];

    rowData.forEach((cell, i) => {
      doc.text(cell, xPos, yPosition);
      xPos += colWidths[i];
    });
    yPosition += 5;
  });

  // Ingredients Section
  yPosition += 10;
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Consumo de Ingredientes", 14, yPosition);

  yPosition += 8;
  doc.setFontSize(8);

  const ingHeaders = ["Ingrediente", "Qtd", "Un", "Custo Médio", "Custo Total"];
  const ingColWidths = [50, 25, 20, 35, 35];
  xPos = 14;

  ingHeaders.forEach((header, i) => {
    doc.text(header, xPos, yPosition);
    xPos += ingColWidths[i];
  });

  yPosition += 5;
  doc.setFont("helvetica", "normal");

  data.ingredientConsumption.slice(0, 20).forEach((ingredient) => {
    if (yPosition > 270) {
      doc.addPage();
      yPosition = 20;
    }

    xPos = 14;
    const rowData = [
      ingredient.ingredientName.substring(0, 25),
      ingredient.quantityConsumed.toFixed(2),
      ingredient.unit,
      formatCurrency(ingredient.averageCost),
      formatCurrency(ingredient.totalCost),
    ];

    rowData.forEach((cell, i) => {
      doc.text(cell, xPos, yPosition);
      xPos += ingColWidths[i];
    });
    yPosition += 5;
  });

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Gerado em ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })} - Página ${i} de ${pageCount}`,
      pageWidth / 2,
      290,
      { align: "center" }
    );
  }

  doc.save(`CMV_${format(new Date(), "yyyy-MM-dd")}.pdf`);
}

export function exportCMVToExcel({ data, dateFrom, dateTo, restaurantName }: ExportParams) {
  const wb = XLSX.utils.book_new();

  // Summary sheet
  const summaryData = [
    ["Relatório CMV - Custo de Mercadoria Vendida"],
    [restaurantName],
    [`Período: ${format(dateFrom, "dd/MM/yyyy")} a ${format(dateTo, "dd/MM/yyyy")}`],
    [],
    ["Métrica", "Valor"],
    ["Receita Total", data.totalRevenue],
    ["Custo Total (CMV)", data.totalCost],
    ["Margem Bruta", data.totalMargin],
    ["% Margem", data.marginPercentage],
  ];
  const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, summaryWs, "Resumo");

  // Products sheet
  const productsData = [
    ["Produto", "Qtd Vendida", "Receita", "Custo", "Margem", "% Margem"],
    ...data.productsCMV.map((p) => [
      p.productName,
      p.quantitySold,
      p.totalRevenue,
      p.totalCost,
      p.margin,
      p.marginPercentage,
    ]),
  ];
  const productsWs = XLSX.utils.aoa_to_sheet(productsData);
  XLSX.utils.book_append_sheet(wb, productsWs, "Produtos");

  // Ingredients sheet
  const ingredientsData = [
    ["Ingrediente", "Qtd Consumida", "Unidade", "Custo Médio", "Custo Total"],
    ...data.ingredientConsumption.map((i) => [
      i.ingredientName,
      i.quantityConsumed,
      i.unit,
      i.averageCost,
      i.totalCost,
    ]),
  ];
  const ingredientsWs = XLSX.utils.aoa_to_sheet(ingredientsData);
  XLSX.utils.book_append_sheet(wb, ingredientsWs, "Ingredientes");

  // Daily CMV sheet
  const dailyData = [
    ["Data", "Receita", "Custo", "Margem", "% Margem"],
    ...data.dailyCMV.map((d) => [
      format(new Date(d.date), "dd/MM/yyyy"),
      d.totalRevenue,
      d.totalCost,
      d.margin,
      d.marginPercentage,
    ]),
  ];
  const dailyWs = XLSX.utils.aoa_to_sheet(dailyData);
  XLSX.utils.book_append_sheet(wb, dailyWs, "CMV Diário");

  XLSX.writeFile(wb, `CMV_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
}
